import React, { useState, useEffect } from 'react';
import { Button, BoxWrapper } from 'components/Base';
import intellifloLogo from '../../../assets/images/intelliflo-logo.png';
import { ReactComponent as HubIcon } from '../../../assets/icons/HubIcon.svg';
import { createStyles, makeStyles, useTheme } from '@material-ui/core/styles';
import { useAuthIo } from 'providers/AuthProvider/AuthIoProvider';
import { Chip } from '@material-ui/core';
import {
  greenBackground,
  greenText,
  linkColor,
  redBackground,
  redText
} from 'utils/colors';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import { compose } from 'redux-starter-kit';
import { connect } from 'react-redux';
import selectors from 'store/selectors';
import LinkCard from './LinkCard';
import LoaderSpinner from 'components/LoaderSpinner/LoaderSpinner';

const useStyles = makeStyles(() =>
  createStyles({
    wrapper: {
      padding: '1rem',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    flex: {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      flexWrap: 'wrap'
    },
    img: {
      maxWidth: '100%',
      marginRight: '1rem'
    },
    flexGrow: {
      flexGrow: 1
    },
    connectedChip: {
      backgroundColor: greenBackground,
      color: greenText
    },
    disconnectedChip: {
      backgroundColor: redBackground,
      color: redText
    },
    icon: {
      fill: greenText
    },
    link: {
      textDecoration: 'none',
      color: linkColor,
      fontWeight: 600,
      '&:hover': {
        textDecoration: 'underline'
      }
    }
  })
);

const IntegrationsTile = ({ advisor }: any) => {
  const theme = useTheme() as any;
  const classes = useStyles();
  const { isAuthenticated, signIn } = useAuthIo();
  const [isLoading, setIsLoading] = useState(true);
  const [hasIntelliflo, setHasIntelliflo] = useState(true);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (advisor.id) {
      setIsLoading(false);
    }

    setHasIntelliflo(
      !!advisor.organization &&
        !!advisor.organization.crm &&
        advisor.organization.crm.includes('intelliflo')
    );

    setIsExpired(advisor.io_expired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisor]);

  return (
    <BoxWrapper title="Live Integrations" noBorder>
      <div className={classes.wrapper}>
        {isLoading ? (
          <LoaderSpinner isLoading={isLoading} />
        ) : (
          <>
            {hasIntelliflo ? (
              <>
                <div className={`${classes.flex} ${classes.flexGrow}`}>
                  <img
                    className={classes.img}
                    width="250"
                    src={intellifloLogo}
                    alt="intelliflo logo"
                  />
                  {isAuthenticated && (
                    <Chip
                      className={classes.connectedChip}
                      icon={<CheckRoundedIcon style={{ fill: greenText }} />}
                      size="small"
                      label="Connected"
                    />
                  )}
                  {!isAuthenticated && isExpired && (
                    <Chip
                      className={classes.disconnectedChip}
                      icon={<ClearRoundedIcon style={{ fill: redText }} />}
                      size="small"
                      label="Disconnected"
                    />
                  )}
                </div>
                <div className={classes.flexGrow}>
                  <div style={{ padding: '1rem 1rem 1rem 0' }}>
                    Seamlessly import your clients and details of their
                    financial plans and underlying investment holdings directly
                    into Fintegrate at the press of a button.
                  </div>
                  {!isAuthenticated && (
                    <Button onClick={() => signIn({})} type="button">
                      Connect
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div>
                <LinkCard
                  clickable={false}
                  icon={
                    <HubIcon
                      style={{
                        fontSize: 48,
                        color: theme.palette.baseColors.secondary
                      }}
                    />
                  }
                  title="No Integrations"
                >
                  <p style={{ maxWidth: '250px' }}>
                    Your organisation has no integrations setup at the moment.
                    Get in touch with us at{' '}
                    <a
                      href="mailto:support@fintegrate.co.uk"
                      className={classes.link}
                    >
                      support@fintegrate.co.uk
                    </a>{' '}
                    to get set up.
                  </p>
                </LinkCard>
              </div>
            )}
          </>
        )}
      </div>
    </BoxWrapper>
  );
};

export default compose(
  connect((state: any) => ({
    advisor: selectors.advisorSelectors.getAdvisor(state)
  }))
)(IntegrationsTile);
